import React, { useMemo } from "react";
import { Button, List, Typography, Skeleton } from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { COUNTER_DATA, CURRENT_INDEX } from "./request/queryKeys";
import { fetchFrequencyCounter } from "../../api/wordsApi";

import { useCreateNewFrequencyMutation } from "./request/createNewFrequencyMutate"; 
import { useDeleteFrequencyByIndexMutation } from "./request/deleteFrequencyByIndexMutate";

type ScanItem = { id: string; label: string };

const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n));

const FrequencySideBar: React.FC = () => {
  const { Text } = Typography;
  const qc = useQueryClient();

  const {
    data: counter,
    isLoading: isCounterLoading,
    isError: isCounterError,
  } = useQuery<number>({
    queryKey: [COUNTER_DATA],
    queryFn: fetchFrequencyCounter,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  const { data: currentIndexRaw = 1 } = useQuery<number>({
    queryKey: [CURRENT_INDEX],
    queryFn: async () => 1, 
    staleTime: Infinity,
    gcTime: Infinity,
  });

  const currentIndex = useMemo(() => {
    const c = counter ?? 0;
    if (c <= 0) return 1;
    return clamp(currentIndexRaw, 1, c);
  }, [currentIndexRaw, counter]);

  const createMut = useCreateNewFrequencyMutation();
  const deleteMut = useDeleteFrequencyByIndexMutation();

  const skeletonRows: ScanItem[] = useMemo(
    () => [{ id: "s1", label: "" }, { id: "s2", label: "" }, { id: "s3", label: "" }],
    []
  );

  const data: ScanItem[] = useMemo(() => {
    const c = counter ?? 0;
    return Array.from({ length: c }, (_, i) => {
      const n = i + 1;
      return { id: String(n), label: `Run ${n}` };
    });
  }, [counter]);

  const showSkeleton = isCounterLoading || isCounterError;
  const isCreating = createMut.isPending;
  const isDeleting = deleteMut.isPending;

  return (
    <div className="h-full w-full min-h-0 flex flex-col p-4 md:p-5">
      <div className="mb-3">
        <p className="text-white/80 font-semibold">header</p>
      </div>

      <Button
        type="primary"
        icon={<PlusOutlined />}
        loading={isCreating}
        disabled={showSkeleton || isDeleting}
        className="
          w-full h-12 rounded-full
          !bg-gradient-to-r !from-[#2f6bff] !to-[#2b90ff]
          !border-0
          shadow-[0_10px_25px_rgba(47,107,255,0.35)]
          font-semibold
        "
        onClick={() => {
          const next = (counter ?? 0) + 1;
          qc.setQueryData([CURRENT_INDEX], next);
          createMut.mutate();
        }}
      >
        Add New Frequency
      </Button>

      <Text className="mt-5 block text-[13px] font-semibold !text-white/70">
        Analyses History
      </Text>

      <div className="mt-3 flex-1 min-h-0 rounded-2xl bg-white/10 border border-white/15 overflow-hidden">
        <div className="h-full overflow-y-auto p-3 space-y-3">
          <List
            dataSource={showSkeleton ? skeletonRows : data}
            split={false}
            renderItem={(item) => {
              const itemIndex = Number(item.id);
              const selected = itemIndex === currentIndex;

              if (showSkeleton) {
                return (
                  <div className="rounded-2xl px-4 border bg-white/5 border-white/10 min-h-[56px] flex items-center">
                    <Skeleton
                      active
                      title={false}
                      paragraph={{ rows: 1, width: "70%" }}
                      className="[&_.ant-skeleton-paragraph>li]:!h-4 [&_.ant-skeleton-paragraph>li]:!rounded-lg [&_.ant-skeleton-paragraph]:!mb-0"
                    />
                  </div>
                );
              }

              return (
                <div
                  onClick={() => qc.setQueryData([CURRENT_INDEX], itemIndex)}
                  className={[
                    "group cursor-pointer select-none",
                    "rounded-2xl px-4 py-3",
                    "border transition",
                    selected
                      ? "bg-[#6db6ff]/25 border-[#6db6ff]/40"
                      : "bg-white/5 border-white/10 hover:bg-white/10",
                  ].join(" ")}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-[16px] font-semibold text-white">{item.label}</span>

                    <Button
                      type="text"
                      icon={<DeleteOutlined />}
                      aria-label={`Delete ${item.label}`}
                      disabled={isCreating || isDeleting}
                      loading={isDeleting && selected}
                      className="
                        !h-9 !w-9 !rounded-xl
                        !text-white/55
                        hover:!text-[#ef4444]
                        hover:!bg-white/10
                        flex items-center justify-center
                      "
                      onClick={(e) => {
                        e.stopPropagation();

                        const c = counter ?? 0;
                        const nextCount = Math.max(0, c - 1);

                        if (nextCount === 0) {
                          qc.setQueryData([CURRENT_INDEX], 1);
                        } else if (currentIndex === itemIndex) {
                          qc.setQueryData([CURRENT_INDEX], Math.max(1, itemIndex - 1));
                        } else if (itemIndex < currentIndex) {
                          qc.setQueryData([CURRENT_INDEX], Math.max(1, currentIndex - 1));
                        }

                        deleteMut.mutate(itemIndex);
                      }}
                    />
                  </div>
                </div>
              );
            }}
          />
        </div>
      </div>

      <div className="pt-4 flex justify-center">
        <div className="h-[5px] w-[120px] rounded-full bg-white/15" />
      </div>
    </div>
  );
};

export default FrequencySideBar;
